import { useCallback, useMemo, useState } from "react";

export type DriverOption = { label: string; id: string };

export type DriverSlotRow = { key: string; value: DriverOption | null };

const newRowKey = () => crypto.randomUUID();

export function useMapAreaDriverSelection() {
  const [slotRows, setSlotRows] = useState<DriverSlotRow[]>(() => [
    { key: newRowKey(), value: null },
  ]);

  const selectedDrivers = useMemo(
    () =>
      slotRows
        .map((row) => row.value)
        .filter((v): v is DriverOption => v !== null),
    [slotRows],
  );

  const resetToDrivers = useCallback((drivers: DriverOption[]) => {
    if (drivers.length === 0) {
      setSlotRows([{ key: newRowKey(), value: null }]);
      return;
    }
    setSlotRows(
      drivers.map((d) => ({
        key: newRowKey(),
        value: d,
      })),
    );
  }, []);

  const setSlotValue = useCallback(
    (rowKey: string, value: DriverOption | null) => {
      setSlotRows((prev) =>
        prev.map((row) => (row.key === rowKey ? { ...row, value } : row)),
      );
    },
    [],
  );

  const addEmptySlot = useCallback(() => {
    setSlotRows((prev) => [...prev, { key: newRowKey(), value: null }]);
  }, []);

  return {
    slotRows,
    resetToDrivers,
    selectedDrivers,
    setSlotValue,
    addEmptySlot,
  };
}
